import { Route, Switch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { DistanceUnit, GetTicketQuery as GetTicket } from '__generated__/graphql';

import EditTicketInfoForm from './EditTicketInfoForm';
import EditTicketPricingForm from './EditTicketPricingForm';
import EditTicketRestrictionsForm from './EditTicketRestrictionsForm';
import EditTicketTeamsForm from './EditTicketTeamsForm';
import EditTicketTimeSlotsForm from './EditTicketTimeSlotsForm';
import EditTicketTypeForm from './EditTicketTypeForm';
import UI from '../../../../common/UI';
import useLocale from '../../../../common/useLocale';
import useProject from '../../../useProject';

type Event = GetTicket['event'];
type Ticket = GetTicket['ticket'];

export interface EditTicketFormProps {
  ticket: Ticket;
  event: Event;
  distanceUnits: DistanceUnit[];
}

const EditTicketForm = ({
  ticket, event, distanceUnits,
}: EditTicketFormProps) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const project = useProject();

  return (
    <UI.FormGrid sc={{ columns: ['1fr', '1fr 3fr'], gutter: 1.5 }}>
      <UI.Nav>
        <UI.NavLink
          to={`/${project.id}/events/${event.id}/tickets/${ticket.id}`}
          exact
          resetScrollPosition={false}
        >
          {t('basic_info')}
        </UI.NavLink>
        <UI.NavLink
          to={`/${project.id}/events/${event.id}/tickets/${ticket.id}/pricing`}
          resetScrollPosition={false}
        >
          {t('pricing')}
          {ticket.promotions.length > 1 && (
            <UI.Small sc={{ muted: true }} style={{ fontWeight: 400 }}>
              {` (${formatNumber(ticket.promotions.length)})`}
            </UI.Small>
          )}
        </UI.NavLink>
        <UI.NavLink
          to={`/${project.id}/events/${event.id}/tickets/${ticket.id}/type`}
          resetScrollPosition={false}
        >
          {t('ticket_type')}
        </UI.NavLink>
        <UI.NavLink
          to={`/${project.id}/events/${event.id}/tickets/${ticket.id}/teams`}
          resetScrollPosition={false}
        >
          {t('teams')}
        </UI.NavLink>
        <UI.NavLink
          to={`/${project.id}/events/${event.id}/tickets/${ticket.id}/restrictions`}
          resetScrollPosition={false}
        >
          {t('restrictions')}
        </UI.NavLink>
        <UI.NavLink
          to={`/${project.id}/events/${event.id}/tickets/${ticket.id}/time-slots`}
          resetScrollPosition={false}
        >
          {t('time_slots')}
          {ticket.time_slots.length > 1 && (
            <UI.Small sc={{ muted: true }} style={{ fontWeight: 400 }}>
              {` (${formatNumber(ticket.time_slots.length)})`}
            </UI.Small>
          )}
        </UI.NavLink>
      </UI.Nav>

      <UI.Div>
        <Switch>
          <Route path="/:projectId/events/:eventId/tickets/:ticketId/pricing">
            <EditTicketPricingForm ticket={ticket} event={event} />
          </Route>
          <Route path="/:projectId/events/:eventId/tickets/:ticketId/type">
            <EditTicketTypeForm ticket={ticket} event={event} />
          </Route>
          <Route path="/:projectId/events/:eventId/tickets/:ticketId/teams">
            <EditTicketTeamsForm ticket={ticket} event={event} />
          </Route>
          <Route path="/:projectId/events/:eventId/tickets/:ticketId/restrictions">
            <EditTicketRestrictionsForm ticket={ticket} event={event} />
          </Route>
          <Route path="/:projectId/events/:eventId/tickets/:ticketId/time-slots">
            <EditTicketTimeSlotsForm ticket={ticket} event={event} />
          </Route>
          <Route path="/:projectId/events/:eventId/tickets/:ticketId">
            <EditTicketInfoForm ticket={ticket} event={event} distanceUnits={distanceUnits} />
          </Route>
        </Switch>
      </UI.Div>
    </UI.FormGrid>
  );
};

export default EditTicketForm;
